import { isString, millisecondsPer } from '@art-suite/art-core-ts';
import { useState } from 'react';

import { ApiReportType, JobLifecycleStatus } from '@kaiizen/kai-pro-api';
import { useCachedQuery } from '@kaiizen/react-persistent-cache';

import { useKaiProApiClient } from '../useKaiProApiClient';
import { pollAsyncJob } from './pollAsyncJob';

// essentially, don't cache the url - 2nd fetches are fast after the report is
// ready, but if the data changed, a new fetch will be needed to trigger the new
// report generation
const DEFAULT_MAX_AGE_MS = millisecondsPer.second * 10;
const TERMINAL_STATUSES: JobLifecycleStatus[] = ['completed', 'failed'];

type ExportJob = {
  lifecycleStatus: JobLifecycleStatus;
  output?: {
    error?: unknown;
    pdfPresignedUrl?: string; // also used for excel presigned URL (TODO - refactor to be just presignedUrl)
  } | undefined;
};

const formatJobOutputError = (output: { error?: unknown }, label: string): Error => {
  if (isString(output.error)) return new Error(`Scan ${label} export result error: ${output.error}`);
  console.error(`Scan ${label} export result error:`, output.error);
  return new Error(`Scan ${label} export result error: Unknown error type (see console for details)`);
};

/**
 * Cache-backed hook for backend async export jobs.
 *
 * Wraps the standard pattern: poll a job until terminal, throw on job-level
 * failure, extract the presigned URL from `job.output.pdfPresignedUrl`. The
 * underlying useCachedQuery reports `isLoading: true` for the duration of
 * polling, errors flow through `error`, and the resulting URL is exposed both
 * as `data` and (renamed for ergonomics) as `url`.
 *
 * The fetch function observes the AbortSignal from useCachedQuery, so the
 * poll loop bails cleanly when the hook unmounts, the key changes, or
 * `enabled` flips false.
 */
export const useCachedAsyncExport = ({
  scanId,
  reportType,
  label,
  pollIntervalMs,
  maxPollTimeMs,
  method,
}: {
  reportType?: ApiReportType;
  scanId: string | undefined;
  /** Format label used in error messages (e.g. "PDF", "Excel"). */
  label: string;
  /** Kicks off / refreshes the export job. Called once per poll cycle. */
  pollIntervalMs?: number;
  maxPollTimeMs?: number;
  method: 'exportScopeReportPdf' | 'exportScopePurchaseOrder';
}) => {
  const [enabled, setEnabled] = useState(false);
  const kaiProApiClient = useKaiProApiClient();
  const key = `${label}-export:${scanId}:${reportType ?? ''}`;
  if (!kaiProApiClient[method]) throw new Error(`Method ${method} not found in KaiProApiClient`);

  const query = useCachedQuery<string>(
    key,
    async (_key, _qc, { signal }) => {
      try {
        console.log('start pollAsyncJob ' + method);
        const job = await pollAsyncJob({
          // Invoke through the client so `this` stays bound — extracting the
          // method into a local first would call it free-standing and the
          // internal `this.jsonRequest` would blow up.
          start: () => kaiProApiClient[method](scanId!, { reportType }) as unknown as Promise<ExportJob>,
          isTerminal: j => TERMINAL_STATUSES.includes(j.lifecycleStatus),
          pollIntervalMs,
          maxPollTimeMs,
          signal,
          jobName: `${label} export preparation`,
        });
        if (job.output?.error) throw formatJobOutputError(job.output, label);
        console.log('pollAsyncJob completed ' + method, job);
        const url = job.output && 'pdfPresignedUrl' in job.output ? job.output.pdfPresignedUrl : undefined;
        if (!url) throw new Error(`No ${label} URL in job output`);
        return url;
      } catch (error) {
        console.error(`${label} export preparation failed:`, error);
        throw error;
      }
    },
    {
      enabled: enabled && !!scanId && !!kaiProApiClient.token,
      maxAgeMs: DEFAULT_MAX_AGE_MS,
    },
  );
  return {
    ...query,

    // Gate the URL on `enabled` so the cached URL from a previous session
    // doesn't auto-trigger downloads on remount — consumers only see a URL
    // after the user calls prepareReport in this session.
    reportUrl: enabled ? query.data : undefined,
    prepareReport: () => {
      console.log('prepareReport ' + method, 'haveData:', !!query.data);
      setEnabled(true);
      if (query.data) query.refetch();
    },
  };
};
